import { Phone, Mail, MessageCircle, MapPin, Globe, Clock, UserPlus, BadgeCheck, ArrowUpRight, Share2, ShieldCheck, Building2 } from "lucide-react";
import { THEMES, SOCIALS, CAMERALI, normalizeUrl, formatDate } from "@/lib/card";
import { fileUrl } from "@/lib/api";

const Action = ({ href, icon: Icon, label, t, accent, onClick, testId }) => (
  <a href={href} target={href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer" onClick={onClick} data-testid={testId}
     className="flex flex-col items-center gap-1.5 rounded-xl py-3 transition-transform duration-200 hover:-translate-y-0.5"
     style={{ background: t.surface, color: t.text }}>
    <Icon size={18} style={{ color: accent }} />
    <span className="text-[11px] font-semibold">{label}</span>
  </a>
);

const Row = ({ icon: Icon, children, t }) => (
  <div className="flex items-start gap-3 text-sm" style={{ color: t.text }}>
    <Icon size={16} className="mt-0.5 shrink-0" style={{ color: t.muted }} />
    <span className="break-words min-w-0">{children}</span>
  </div>
);

const CameraliBlock = ({ card, t, accent }) => {
  const rows = CAMERALI.filter(([k]) => card[k] && !(k === "stato_attivita" && CAMERALI.every(([o]) => o === k || !card[o])));
  if (!rows.length) return null;
  return (
    <div className="mt-6 rounded-xl border p-4" style={{ borderColor: t.border }} data-testid="card-camerali">
      <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: accent }}><Building2 size={14} /> Dati Camerali</p>
      <dl className="mt-3 space-y-2.5">
        {rows.map(([k, label]) => (
          <div key={k} className="flex justify-between gap-4 text-xs">
            <dt style={{ color: t.muted }}>{label}</dt>
            <dd className="text-right font-medium break-words min-w-0" data-testid={`card-camerali-${k}`}>{k === "data_costituzione" ? formatDate(card[k]) : card[k]}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

export const CardView = ({ card, onEvent = () => {}, onVCard, onShare, coverFallback }) => {
  const t = THEMES[card.theme] || THEMES.classic_executive;
  const accent = card.accent || "#2563EB";
  const onAccent = accent.toUpperCase() === "#F8FAFC" ? "#0F172A" : "#FFFFFF";
  const cover = fileUrl(card.cover_path) || coverFallback;
  const logo = fileUrl(card.logo_path) || card.logo_url;
  const wa = card.whatsapp?.replace(/[^\d]/g, "");
  const actions = [
    card.phone && { href: `tel:${card.phone}`, icon: Phone, label: "Chiama", key: "telefono" },
    card.email && { href: `mailto:${card.email}`, icon: Mail, label: "Email", key: "email" },
    wa && { href: `https://wa.me/${wa}`, icon: MessageCircle, label: "WhatsApp", key: "whatsapp" },
    card.address && { href: `https://maps.google.com/?q=${encodeURIComponent(card.address)}`, icon: MapPin, label: "Mappa", key: "mappa" },
  ].filter(Boolean);
  const socials = SOCIALS.filter((s) => card.socials?.[s.key]);

  return (
    <div data-testid="card-view" className="w-full overflow-hidden" style={{ background: t.bg, color: t.text }}>
      <div className="relative h-40" style={{ background: cover ? undefined : `linear-gradient(135deg, ${accent}, ${t.surface})` }}>
        {cover && <img src={cover} alt="" className="h-full w-full object-cover" />}
        <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, transparent 40%, ${t.bg})` }} />
        {onShare && (
          <button onClick={onShare} data-testid="public-card-share-qr-button" aria-label="Condividi"
                  className="absolute right-4 top-4 rounded-full bg-black/40 p-2.5 text-white backdrop-blur-md transition-colors hover:bg-black/60">
            <Share2 size={16} />
          </button>
        )}
      </div>
      <div className="relative -mt-12 px-6 pb-8">
        <div className="h-24 w-24 overflow-hidden rounded-2xl border-4 shadow-xl" style={{ borderColor: t.bg, background: t.surface }}>
          {logo ? <img src={logo} alt="Logo" className="h-full w-full object-cover" />
            : <div className="flex h-full w-full items-center justify-center font-display text-3xl font-bold" style={{ color: accent }}>
                {(card.company_name || "I").charAt(0).toUpperCase()}</div>}
        </div>
        <div className="mt-4 flex items-center gap-2">
          <h2 data-testid="card-company-name" className="font-display text-2xl font-bold leading-tight tracking-tight">{card.company_name || "Nome Impresa"}</h2>
          <BadgeCheck size={20} style={{ color: accent }} className="shrink-0" />
        </div>
        {(card.contact_name || card.role) && (
          <p className="mt-1 text-sm" style={{ color: t.muted }}>{[card.contact_name, card.role].filter(Boolean).join(" · ")}</p>
        )}
        {card.piva && (
          <span className="mt-3 inline-block rounded-full border px-3 py-1 font-mono-ui text-[11px]" style={{ borderColor: t.border, color: t.muted }}>
            P.IVA {card.piva}</span>
        )}

        {actions.length > 0 && (
          <div className="mt-6 grid gap-2" style={{ gridTemplateColumns: `repeat(${actions.length}, minmax(0,1fr))` }}>
            {actions.map(({ key, ...a }) => <Action key={key} {...a} t={t} accent={accent} testId={`card-action-${key}`} onClick={() => onEvent("click", key)} />)}
          </div>
        )}

        <button onClick={onVCard} data-testid="public-card-vcard-button"
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-bold transition-transform duration-200 hover:scale-[1.01] active:scale-[0.99]"
                style={{ background: accent, color: onAccent }}>
          <UserPlus size={18} /> Salva in Rubrica
        </button>

        {card.description && <p className="mt-6 whitespace-pre-line text-sm leading-relaxed" style={{ color: t.muted }}>{card.description}</p>}

        {(card.website || card.hours || card.address || card.pec) && (
          <div className="mt-6 space-y-3 rounded-xl p-4" style={{ background: t.surface }}>
            {card.pec && <Row icon={ShieldCheck} t={t}><a href={`mailto:${card.pec}`} onClick={() => onEvent("click", "pec")} data-testid="card-pec-link" className="underline-offset-4 hover:underline">PEC: {card.pec}</a></Row>}
            {card.website && <Row icon={Globe} t={t}><a href={normalizeUrl(card.website)} target="_blank" rel="noreferrer" onClick={() => onEvent("click", "sito web")} data-testid="card-website-link" className="underline-offset-4 hover:underline">{card.website.replace(/^https?:\/\//, "")}</a></Row>}
            {card.address && <Row icon={MapPin} t={t}>{card.address}</Row>}
            {card.hours && <Row icon={Clock} t={t}><span className="whitespace-pre-line">{card.hours}</span></Row>}
          </div>
        )}

        <CameraliBlock card={card} t={t} accent={accent} />

        {card.links?.length > 0 && (
          <div className="mt-6 space-y-2">
            {card.links.map((l) => (
              <a key={l.id} href={normalizeUrl(l.url)} target="_blank" rel="noreferrer" onClick={() => onEvent("click", l.label)} data-testid={`card-link-${l.id}`}
                 className="group flex items-center justify-between rounded-xl border px-4 py-3.5 text-sm font-semibold transition-colors duration-200"
                 style={{ borderColor: t.border, color: t.text }}>
                {l.label}<ArrowUpRight size={16} className="transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" style={{ color: accent }} />
              </a>
            ))}
          </div>
        )}

        {socials.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2">
            {socials.map(({ key, icon: Icon, label }) => (
              <a key={key} href={normalizeUrl(card.socials[key])} target="_blank" rel="noreferrer" aria-label={label} onClick={() => onEvent("click", label)} data-testid={`card-social-${key}`}
                 className="rounded-full p-3 transition-transform duration-200 hover:-translate-y-0.5" style={{ background: t.surface, color: t.text }}>
                <Icon size={18} />
              </a>
            ))}
          </div>
        )}

        <p className="mt-10 text-center text-[11px] tracking-wide" style={{ color: t.muted }}>
          Creato con <span className="font-semibold">taptolink.it</span>
        </p>
      </div>
    </div>
  );
};

export const PhoneFrame = ({ children }) => (
  <div className="mx-auto w-full max-w-[360px] rounded-[2.75rem] border-[10px] border-slate-900 bg-slate-900 shadow-[0_40px_80px_-20px_rgba(15,23,42,.45)]">
    <div className="h-[640px] overflow-y-auto rounded-[2rem] bg-white [scrollbar-width:none]">{children}</div>
  </div>
);
